import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Badge,
  Button,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Link as MuiLink,
} from '@mui/material';
import { Close, FilterAltOutlined, North, Search, South, Refresh } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import { AppliedFilterChips } from '../filters';
import { getComparator, stableSort } from '../../../lib/utils';
import PaginationComponent from './PaginationComponent';
import TableLoader from '../TableLoader';
import PaginationInfo from './PaginationInfo';
import TableColumnManager from './TableColumnManager';
import { tokens } from '../../../mui-theme';

const defaultIsRowDisabled = () => false;

/** Empty state row */
const EmptyRow = ({ colSpan, tablekey }) => (
  <TableRow>
    <TableCell
      colSpan={colSpan}
      sx={{
        textAlign: 'center',
        py: 8,
        color: 'text.secondary',
        fontSize: '0.875rem',
        border: 'none',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No {tablekey || 'data'} available
        </Typography>
      </Box>
    </TableCell>
  </TableRow>
);

/** Sortable header cell content */
const SortLabel = ({ label, isSorted, order }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
    }}
  >
    {label}
    {isSorted && (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          color: 'primary.main',
          ml: 0.25,
        }}
      >
        {order === 'asc' ? (
          <North sx={{ fontSize: 12 }} />
        ) : (
          <South sx={{ fontSize: 12 }} />
        )}
      </Box>
    )}
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// TableComponent
// ─────────────────────────────────────────────────────────────────────────────

const TableComponent = ({
  data,
  headerTabs,
  page,
  perPage,
  showCheckbox,
  action,
  defaultSort,
  order,
  setOrder,
  selected,
  setSelected,
  showPages,
  striped,
  isLoading,
  setSelectedRows = () => { },
  totalCount,
  handlePageChange,
  tablekey,
  isRowDisabled = defaultIsRowDisabled,
  checkboxDisabledTitle,
  showCount,
  setPerPage,
  showRowCount = false,
  showColumnManager = false,
  selectedColumns,
  setSelectedColumns,
  mandatoryKeys = null,
  serverSideSorting = false,
  onSortChange = () => { },
  // ── Search props ────────────────────────────────────────────────────────────
  // showSearch    — renders a search input above the table
  // onSearch      — (value: string) => void  called on input change (debounced)
  // searchPlaceholder — placeholder text for the search input
  // searchDebounce — debounce delay in ms (default: 300)
  showSearch = false,
  onSearch,
  searchPlaceholder = 'Search…',
  searchDebounce = 300,
  // ── Filter props ────────────────────────────────────────────────────────────
  // showFilter      — renders a filter button that opens the modal
  // filterModal     — render prop: (open, onClose) => <YourFilterModal />
  //                   keeps TableComponent fully decoupled from filter implementation
  // appliedFilters  — props forwarded to AppliedFilterChips (filterConfig, optionsMap, etc.)
  //                   when provided, chip row is shown below the toolbar
  // activeFilterCount — number shown on the filter button badge
  showFilter = false,
  filterModal,
  appliedFilters,
  activeFilterCount = 0,
  filterConfig = [],
  optionsMap = {},
  defaultColumns = null,
  onRefresh = null,
}) => {
  const [orderBy, setOrderBy] = React.useState(defaultSort || '');
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const debounceTimer = useRef(null);

  // Debounced search — calls onSearch after the user stops typing
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchValue(value);
    if (!onSearch) return;
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => onSearch(value), searchDebounce);
  }, [onSearch, searchDebounce]);

  const handleSearchClear = () => {
    setSearchValue('');
    onSearch?.('');
  };

  // Clean up debounce on unmount
  useEffect(() => () => clearTimeout(debounceTimer.current), []);

  // ── Derived state ──────────────────────────────────────────────────────────
  const visibleHeaders = useMemo(() => {
    if (!showColumnManager || !selectedColumns?.length) return headerTabs;
    return headerTabs.filter((header) => selectedColumns.includes(header.key));
  }, [headerTabs, selectedColumns, showColumnManager]);

  const isAllSelected = useMemo(() => {
    const selectableRows =
      data?.filter((row) => !isRowDisabled(row) && !row.isRowDisabled) || [];
    const selectableIds = selectableRows.map((r) => r.id);
    if (selectableIds.length === 0) return false;
    return selectableIds.every((id) => selected?.includes(id));
  }, [data, selected, isRowDisabled]);

  const isSomeSelected = useMemo(() => {
    if (!selected?.length) return false;
    const selectableRows =
      data?.filter((row) => !isRowDisabled(row) && !row.isRowDisabled) || [];
    const selectableIds = selectableRows.map((r) => r.id);
    return selectableIds.some((id) => selected.includes(id)) && !isAllSelected;
  }, [data, selected, isRowDisabled, isAllSelected]);

  const colSpan =
    visibleHeaders.length + (action ? 1 : 0) + (showCheckbox ? 1 : 0);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const isSelected = (id) => selected && selected.indexOf(id) !== -1;

  const handleClick = (event, name, row) => {
    if (isRowDisabled(row)) return;
    const selectedIndex = selected?.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
    setSelectedRows((prevRows) => {
      const combined = [...prevRows, ...data];
      const unique = Array.from(
        new Map(combined.map((r) => [r.id, r])).values()
      );
      return unique.filter((r) => newSelected.includes(r.id));
    });
  };

  const handleSelectAll = (checked) => {
    const selectableRows =
      data?.filter((row) => !isRowDisabled(row) && !row.isRowDisabled) || [];
    const selectableIds = selectableRows.map((r) => r.id);
    if (checked) {
      const mergedSelections = Array.from(
        new Set([...selected, ...selectableIds])
      );
      setSelected(mergedSelections);
      setSelectedRows((prevRows) => {
        const combined = [...prevRows, ...selectableRows];
        const unique = Array.from(
          new Map(combined.map((r) => [r.id, r])).values()
        );
        return unique.filter((r) => mergedSelections.includes(r.id));
      });
    } else {
      setSelected(selected.filter((id) => !selectableIds.includes(id)));
      setSelectedRows((prev) =>
        prev.filter((row) => !selectableIds.includes(row.id))
      );
    }
  };

  const handleSort = (key) => {
    const newOrder = order === 'asc' ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(key);
    if (serverSideSorting) {
      onSortChange(key, newOrder);
    }
  };

  const hasTopBar = showSearch || showFilter;
  const hasAppliedFilters = appliedFilters && activeFilterCount > 0;

  return (
    <Box sx={{ mt: 3, width: '100%' }}>
      {/* ── Search + Filter bar ──────────────────────────────────────────── */}
      {hasTopBar && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: hasAppliedFilters ? 1 : 2,
          }}
        >
          {showSearch && (
            <TextField
              size="small"
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={handleSearchChange}
              sx={{
                flex: 1,
                maxWidth: 360,
                '& .MuiOutlinedInput-root': { 
                  height: 36,
                  backgroundColor: 'background.paper',
                  paddingLeft: '12px',
                  fontSize: '0.8125rem',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'divider',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                    borderWidth: '1px',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '8px 12px 8px 0',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 17, color: 'text.disabled' }} />
                  </InputAdornment>
                ),
                endAdornment: searchValue && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleSearchClear} edge="end" tabIndex={-1}>
                      <Close sx={{ fontSize: 14 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}

          {showFilter && (
            <Badge
              badgeContent={activeFilterCount}
              color="primary"
              sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', height: 16, minWidth: 16 } }}
            >
              <Button
                variant={activeFilterCount > 0 ? 'contained' : 'outlined'}
                size="small"
                startIcon={<FilterAltOutlined sx={{ fontSize: 16 }} />}
                onClick={() => setFilterOpen(true)}
                sx={{ height: 36, fontSize: '0.8125rem', whiteSpace: 'nowrap' }}
              >
                Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
              </Button>
            </Badge>
          )}
        </Box>
      )}

      {/* ── Applied filter chips ─────────────────────────────────────────── */}
      {hasAppliedFilters && (
        <Box sx={{ mb: 1.5 }}>
          <AppliedFilterChips
            filterConfig={filterConfig}
            optionsMap={optionsMap}
            {...appliedFilters}
          />
        </Box>
      )}

      {/* ── Filter modal (render prop — decoupled from implementation) ───── */}
      {showFilter && filterModal && filterModal(filterOpen, () => setFilterOpen(false))}

      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      {(
        selected?.length > 0 ||
        showCount ||
        showColumnManager ||
        onRefresh
      ) && (
      <Box
        sx={{
          display: 'flex',
          justifyContent: selected?.length > 0 ? 'space-between' : 'flex-end',
          alignItems: 'center',
          mb: 1.5,
          gap: 1,
          minHeight: 36,
        }}
      >
        {/* Selection badge */}
        {selected?.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 1.5,
              py: 0.5,
              borderRadius: 'sm',
              backgroundColor: alpha(tokens.colors.primary[500], 0.07),
              border: `1px solid ${alpha(tokens.colors.primary[500], 0.18)}`,
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: 'primary.main', fontWeight: 600, mb: 0 }}
            >
              {selected.length}{' '}
              {selected.length === 1 ? 'record' : 'records'} selected
            </Typography>
            <Tooltip title="Clear selection" arrow>
              <IconButton
                size="small"
                onClick={() => {
                  setSelected([]);
                  setSelectedRows([]);
                }}
                sx={{
                  border: '1px solid',
                  borderColor: 'error.main',
                  color: 'error.main',
                  padding: '2px',
                  '&:hover': {
                    backgroundColor: alpha(tokens.colors.error.main, 0.06),
                  },
                }}
              >
                <Close sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Right-side controls */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {showCount && (
            <PaginationInfo
              currentPage={page}
              perPage={perPage}
              totalCount={totalCount}
              showText=""
              showCenter={false}
              setPerPage={setPerPage}
              showRowCount={showRowCount}
            />
          )}
          {showColumnManager && (
            <TableColumnManager
              allHeaders={headerTabs}
              selectedKeys={selectedColumns}
              onSelectionChange={setSelectedColumns}
              mandatoryKeys={mandatoryKeys || ['key', 'summary']}
              defaultKeys={defaultColumns ?? headerTabs.map((h) => h.key)}
            />
          )}
          {onRefresh && (
            <Tooltip title="Refresh data">
              <IconButton
                onClick={onRefresh}
                disableRipple
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  height: 36,
                  width: 36,
                  color: 'text.secondary',
                  '&:hover': {
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    backgroundColor: alpha(tokens.colors.primary[500], 0.05),
                  }
                }}
              >
                <Refresh sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
      )}

      {/* ── Table ───────────────────────────────────────────────────────── */}
      <TableContainer>
        <Table stickyHeader aria-label={tablekey || 'data table'}>
          {/* Head */}
          <TableHead>
            <TableRow>
              {showCheckbox && !isLoading && (
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={isAllSelected}
                    indeterminate={isSomeSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    inputProps={{ 'aria-label': 'select all rows' }}
                  />
                </TableCell>
              )}
              {visibleHeaders.map((tab) => (
                <TableCell
                  key={tab.key}
                  title={tab.hoverText || tab.label}
                  onClick={
                    tab?.sortable ? () => handleSort(tab.key) : undefined
                  }
                  sx={{
                    minWidth: tab.minWidth || 100,
                    cursor: tab?.sortable ? 'pointer' : 'default',
                    color:
                      tab?.sortable && tab.key === orderBy
                        ? 'primary.main'
                        : 'inherit',
                    '&:hover': tab?.sortable
                      ? { color: 'primary.main' }
                      : {},
                  }}
                >
                  <SortLabel
                    label={tab.label}
                    isSorted={tab?.sortable && tab.key === orderBy}
                    order={order}
                  />
                </TableCell>
              ))}
              {action && <TableCell />}
            </TableRow>
          </TableHead>

          {/* Body */}
          {isLoading ? (
            <TableLoader
              colSpan={colSpan}
            />
          ) : (
            <TableBody>
              {data?.length === 0 && (
                <EmptyRow colSpan={colSpan} tablekey={tablekey} />
              )}

              {(serverSideSorting
                ? data
                : stableSort(data, getComparator(order, orderBy))
              ).map((row, rowIndex) => {
                const isItemSelected = isSelected(row.id);
                const disabled = isRowDisabled(row);

                return (
                  <TableRow
                    key={row.id}
                    selected={isItemSelected}
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    className={disabled ? 'row-disabled' : ''}
                    title={
                      row.isRowDisabled
                        ? checkboxDisabledTitle
                        : disabled
                          ? 'Already added'
                          : ''
                    }
                    sx={
                      striped && rowIndex % 2 !== 0
                        ? { backgroundColor: tokens.table.stripedRow }
                        : {}
                    }
                  >
                    {showCheckbox && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={!!isItemSelected || disabled}
                          onChange={(event) =>
                            handleClick(event, row.id, row)
                          }
                          disabled={disabled || row.isRowDisabled}
                          inputProps={{
                            'aria-label': `select row ${row.id}`,
                          }}
                        />
                      </TableCell>
                    )}

                    {visibleHeaders.map(
                      (
                        { key, linkToId, formatContent },
                        index
                      ) => (
                        <TableCell key={'col' + index + rowIndex}>
                          {formatContent ? (
                            formatContent(row, rowIndex, page, perPage)
                          ) : linkToId ? (
                            <MuiLink
                              component={RouterLink}
                              to={`/${linkToId}/${row.id}`}
                              sx={{
                                color: 'primary.main',
                                fontWeight: 500,
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              {row[key]}
                            </MuiLink>
                          ) : (
                            row[key] || '-'
                          )}
                        </TableCell>
                      )
                    )}

                    {action && (
                      <TableCell align="right" className="row-action">
                        {action(row)}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          )}
        </Table>
      </TableContainer>

      {/* ── Pagination ──────────────────────────────────────────────────── */}
      {showPages && (
        <PaginationComponent
          totalCount={totalCount}
          currentPage={page}
          handlePageChange={handlePageChange}
          perPage={perPage}
        />
      )}
    </Box>
  );
};

export default TableComponent;